// Small pieces shared by the lenders screens: the logo tile, the status badge, the single switch with its dialog.
import { useState } from 'react'
import { Badge, Field, Modal, notify } from '@/kit/ui'
import { inApp } from '@/lib/inApp'
import { STATUS_LABEL, statusLabel, switchImpact } from '@/lib/lender'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useHubStore } from '@/store/hubStore'
import type { Lender } from '@/types'

/** The uploaded logo (on the Hub API), else the name's first letters on the brand colour. */
export function LenderLogo({ lender, size = 44 }: { lender: Lender; size?: number }) {
  const [broken, setBroken] = useState(false)
  if (lender.hasLogo && inApp.available() && !broken) {
    return <img src={`${inApp.fileUrl(lender.slug, 'logo')}?t=${encodeURIComponent(lender.updatedAt)}`} alt="" onError={() => setBroken(true)} className="shrink-0 rounded-md border border-slate-200 bg-white object-contain p-0.5 dark:border-slate-700" style={{ width: size, height: size }} />
  }
  const letters = lender.name.replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?'
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-md font-bold text-white"
      style={{ width: size, height: size, background: lender.brand.color, fontSize: Math.round(size * 0.32) }}
      aria-hidden="true"
    >
      {letters}
    </span>
  )
}

export function StatusPill({ lender }: { lender: Lender }) {
  const tone = lender.status === 'active' ? 'success' : lender.status === 'off' ? 'danger' : 'gray'
  const text = lender.status === 'active' ? `${statusLabel(lender)} · since ${lender.activeSince}` : lender.status === 'off' ? `Off · ${lender.offReason ?? ''}` : 'Draft'
  return <Badge tone={tone}>{text}</Badge>
}

/**
 * The one Active switch (owner, 2026-10-17): the only place a lender is turned on or off. Opens the dialog that
 * lists what happens on every surface and asks for a reason; the store writes History, an Alert and the event.
 */
export function ActiveSwitch({ lender, asButton = false }: { lender: Lender; asButton?: boolean }) {
  const { user, can } = useCurrentUser()
  const setStatus = useHubStore((s) => s.setLenderStatus)
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string>()
  const on = lender.status === 'active'
  const allowed = can('lenders.activate') && lender.status !== 'draft'
  const turningOn = !on

  const confirm = () => {
    if (reason.trim().length < 5) return setError('Write a reason of at least 5 characters; it goes to History and Alerts.')
    setStatus(lender.slug, turningOn, reason.trim(), user.email)
    notify[turningOn ? 'success' : 'warning'](turningOn ? `${lender.name} is On everywhere` : `${lender.name} is Off everywhere`, turningOn ? 'Endpoints resumed now; the app follows within 5 minutes.' : 'Endpoints suspended now; the app drops it within 5 minutes.')
    setOpen(false)
    setReason('')
    setError(undefined)
  }

  if (lender.status === 'draft' && !asButton) return <Badge tone="gray">Draft</Badge>

  return (
    <>
      {asButton ? (
        <button type="button" className={`btn ${on ? 'btn-danger' : 'btn-primary'}`} disabled={!allowed} onClick={() => setOpen(true)} title={allowed ? undefined : 'Admins only; a draft is activated from Add a lender'}>
          {on ? 'Deactivate everywhere…' : 'Activate everywhere…'}
        </button>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={`${lender.name}: ${STATUS_LABEL[lender.status]}`}
          disabled={!allowed}
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-2 text-xs font-medium ${allowed ? '' : 'cursor-not-allowed opacity-60'}`}
          title={allowed ? 'Switch on or off everywhere' : 'Admins only'}
        >
          <span className={`relative h-5 w-9 shrink-0 rounded-full transition ${on ? 'bg-primary' : 'bg-red-500'}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${on ? 'left-[18px]' : 'left-0.5'}`} />
          </span>
          {on ? 'On' : 'Off'}
        </button>
      )}
      <Modal isOpen={open} onClose={() => setOpen(false)} title={turningOn ? `Activate ${lender.name} everywhere?` : `Deactivate ${lender.name} everywhere?`} size="lg">
        <p className="mb-3 text-xs text-muted">The single switch. Nothing is deleted; the opposite switch later puts everything back the same way.</p>
        <div className="mb-3 space-y-1.5">
          {switchImpact(lender, turningOn).map((i) => (
            <div key={i.surface} className="grid grid-cols-[110px_1fr] gap-2 rounded bg-slate-50 px-2.5 py-1.5 text-xs dark:bg-slate-900">
              <b className="font-semibold">{i.surface}</b>
              <span>{i.text}</span>
            </div>
          ))}
        </div>
        <Field label="Reason (required; goes to History and Alerts)" error={error}>
          {(id) => <input id={id} className={`input ${error ? 'input-error' : ''}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. lender API outage since 08:50" autoFocus />}
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
          <button type="button" className={`btn ${turningOn ? 'btn-primary' : 'btn-danger'}`} onClick={confirm} data-testid="confirm-switch">
            {turningOn ? 'Activate everywhere' : 'Deactivate everywhere'}
          </button>
        </div>
      </Modal>
    </>
  )
}
